import { CalendarClock, MapPin, User } from "lucide-react";
import ResourcePage from "@/components/ResourcePage";
import { makeAdapter } from "@/lib/supabaseAdapter";

const adapter = makeAdapter({
  table: "appointments",
  filterColumn: "profile_id",
  orderBy: "appointment_date",
  ascending: false,
});

export default function Appointments() {
  return (
    <ResourcePage
      config={{
        adapter,
        filterBy: "profile_id",
        eyebrow: "Schedule",
        title: "Appointments",
        description: "Upcoming visits, specialists, and follow-ups in one place.",
        icon: CalendarClock,
        accent: "bg-sand/30 text-foreground",
        addLabel: "Add appointment",
        testIdPrefix: "appt",
        blank: () => ({
          profile_id: "",
          title: "",
          doctor_name: "",
          location: "",
          appointment_date: "",
          appointment_time: "",
          reminder_minutes: 30,
          notes: "",
          status: "scheduled",
        }),
        transform: (f, { profile }) => ({
          profile_id: f.profile_id,
          profile_name: profile?.name || null,
          title: f.title,
          doctor_name: f.doctor_name || null,
          location: f.location || null,
          appointment_date: f.appointment_date,
          appointment_time: f.appointment_time || null,
          reminder_minutes: f.reminder_minutes ? Number(f.reminder_minutes) : 30,
          notes: f.notes || null,
          status: f.status || "scheduled",
        }),
        fields: [
          { key: "title", label: "Title", required: true, span: 2, placeholder: "e.g. Pediatrician check-up" },
          { key: "appointment_date", label: "Date", type: "date", required: true },
          { key: "appointment_time", label: "Time", type: "time" },
          { key: "reminder_minutes", label: "Reminder (min before)", type: "number", placeholder: "30" },
          {
            key: "status",
            label: "Status",
            type: "select",
            options: [
              { value: "scheduled", label: "Scheduled" },
              { value: "completed", label: "Completed" },
              { value: "cancelled", label: "Cancelled" },
            ],
          },
          { key: "doctor_name", label: "Doctor / provider", placeholder: "Dr. Patel" },
          { key: "location", label: "Location", placeholder: "Clinic name / address" },
          { key: "notes", label: "Notes", type: "textarea" },
        ],
        render: (a) => {
          const date = a.appointment_date || "";
          const time = a.appointment_time ? ` at ${a.appointment_time.slice(0, 5)}` : "";
          const statusStyle = {
            scheduled: "bg-primary/10 text-primary",
            completed: "bg-emerald-100 text-emerald-700",
            cancelled: "bg-destructive/10 text-destructive",
          }[a.status] || "bg-secondary text-foreground";
          return (
            <div>
              <div className="font-heading text-lg font-bold leading-tight">{a.title}</div>
              <div className="mt-1 text-sm text-muted-foreground">
                {date}{time}
              </div>
              {a.doctor_name && (
                <div className="mt-2 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <User className="h-3.5 w-3.5" /> {a.doctor_name}
                </div>
              )}
              {a.location && (
                <div className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <MapPin className="h-3.5 w-3.5" /> {a.location}
                </div>
              )}
              <span className={`mt-3 inline-block rounded-full px-3 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${statusStyle}`}>
                {a.status}
              </span>
              {a.notes && (
                <div className="mt-3 text-xs text-muted-foreground">{a.notes}</div>
              )}
            </div>
          );
        },
      }}
    />
  );
}
